import { Link } from 'react-router';
import { SEO, BreadcrumbSchema, FAQSchema, ServiceSchema, SpeakableSchema } from '@/components/seo/seo';

function RatingBadge({ rating, size = 'md' }: { rating: 'red' | 'amber' | 'green'; size?: 'sm' | 'md' | 'lg' }) {
  const colors = {
    red: 'bg-rose-500 text-white',
    amber: 'bg-amber-500 text-white',
    green: 'bg-emerald-500 text-white',
  };
  const sizes = {
    sm: 'px-2 py-0.5 text-xs',
    md: 'px-3 py-1 text-sm',
    lg: 'px-4 py-2 text-base',
  };
  return (
    <span className={`inline-block font-bold rounded-full uppercase ${colors[rating]} ${sizes[size]}`}>
      {rating}
    </span>
  );
}

function QuoteCard({ quote, source }: { quote: string; source: string }) {
  return (
    <blockquote className="bg-surface-50 border-l-4 border-brand-500 p-6 rounded-r-xl">
      <p className="text-surface-800 italic">&ldquo;{quote}&rdquo;</p>
      <cite className="text-sm text-surface-600 mt-2 block not-italic">— {source}</cite>
    </blockquote>
  );
}

export function MedwayCouncilPage() {
  return (
    <div className="min-h-screen">
      <SEO
        title="Medway Pothole Claims | 31% U-Roads RED | Fixtyer"
        description="Medway Council reports 31% of unclassified roads in RED condition, surveys residential streets once every four years, and earns a RED DfT best-practice scorecard. See what the AMBER rating means for your pothole claim."
        canonical="/councils/medway"
      />
      <BreadcrumbSchema
        items={[
          { name: 'Home', url: '/' },
          { name: 'Councils', url: '/councils' },
          { name: 'Medway', url: '/councils/medway' },
        ]}
      />
      <FAQSchema
        faqs={[
          {
            question: 'Medway has a RED best-practice scorecard — can I still claim?',
            answer: 'Yes. The DfT Best Practice rating is RED, and Medway\'s overall rating is AMBER — but your claim still turns on whether the specific defect that damaged your vehicle was reasonably inspected and repaired under Section 58, not on scorecard colours alone. Medway\'s own transparency report shows 31% of U-roads in RED condition in 2024, coarse visual inspections on a four-year rotation across 629km of residential roads, and the council admitting its current strategy is insufficient to improve unclassified roads.',
          },
          {
            question: 'What if my pothole was on a residential or unclassified road?',
            answer: 'U-roads make up 629.37km — 74% of Medway\'s 845km network. The council conducts coarse visual inspections divided into four areas, with each area surveyed once every four years on a rotational basis. There is no nationally recognised standard for U-road condition monitoring. At the last comparable surveys, 19–31% of U-roads were in RED condition — roughly 120–195km of estate streets, closes and local routes.',
          },
          {
            question: 'Pothole repairs fell from 16,373 to 7,252 — does that mean the roads are fixed?',
            answer: 'No. Medway changed its repair methodology in 2024/25, discontinuing cold lay (Viafix) as a permanent repair and shifting to larger patching works. The lower published repair count reflects a different counting approach, not elimination of defects. The council still acknowledges clusters of defects below its safety intervention threshold that visually make the network look worse than it structurally is.',
          },
          {
            question: 'Why is the 2022 U-road figure of 34% unreliable?',
            answer: 'Medway\'s own report states the 2022 condition survey was carried out during an unsuitable time of year, specifically following the winter period. As a result, the data collected was incomparable and unusable, as it did not accurately reflect the true condition of the road network at that time. Do not treat the 34% spike as evidence of sudden recovery or deterioration — the council explicitly disclaims it.',
          },
          {
            question: 'Medway says some defects do not meet its safety threshold — does that help my claim?',
            answer: 'It can. Medway uses additional highways funding to address clusters of defects that do not yet meet its safety intervention threshold or resurfacing criteria, but visually make the network look in worse condition than it structurally is. If the pothole that damaged your vehicle was visible, reportable and left unrepaired, the council\'s own admission that sub-threshold clusters persist is relevant context — especially combined with prior reports and photographic evidence.',
          },
          {
            question: 'Do I have to report the pothole to Medway before claiming?',
            answer: 'Medway\'s own report-a-pothole guidance states that if you have experienced injury or damage because of a pothole, you can make a claim to Medway Council for personal injury or damage — but you must do this once you have reported the pothole to the council. Reporting creates a dated record that strengthens your case if the defect was known before your incident or left unrepaired within a reasonable time.',
          },
        ]}
      />
      <ServiceSchema
        councilName="Medway Council"
        councilSlug="medway"
        rating="amber"
      />
      <SpeakableSchema
        url="/councils/medway"
        headline="Medway Road Maintenance Report"
        cssSelectors={['[data-speakable="summary"]', 'h1', 'h2']}
      />

      {/* Hero */}
      <section className="relative bg-brand-950 text-white overflow-hidden">
        {/* Top chevrons */}
        <div
          className="absolute top-0 left-0 right-0 h-8 z-10"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #f97316,
              #f97316 40px,
              #1e40af 40px,
              #1e40af 80px
            )`,
          }}
        />

        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-16">
          <nav className="text-sm text-brand-400 mb-8 flex items-center gap-2">
            <Link to="/" className="hover:text-white transition-colors">Home</Link>
            <span>/</span>
            <Link to="/councils" className="hover:text-white transition-colors">Councils</Link>
            <span>/</span>
            <span className="text-white">Medway</span>
          </nav>

          <div className="flex flex-wrap items-center gap-3 mb-4">
            <RatingBadge rating="amber" size="lg" />
            <span className="text-brand-300">Overall</span>
            <span className="text-brand-500">|</span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="amber" size="sm" /> <span className="text-brand-300 text-sm">Condition</span>
            </span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="amber" size="sm" /> <span className="text-brand-300 text-sm">Spend</span>
            </span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="red" size="sm" /> <span className="text-brand-300 text-sm">Best Practice</span>
            </span>
          </div>

          <h1 className="font-display text-4xl sm:text-5xl font-bold leading-tight mb-6">
            Medway: A Third of Residential Roads in RED Condition
          </h1>

          <p className="text-xl text-brand-200 max-w-2xl mb-8 leading-relaxed" data-speakable="summary">
            Medway Council&apos;s own transparency data shows{' '}
            <strong className="text-rose-400">31% of unclassified roads in RED condition</strong> in 2024 — up
            from 20% in 2020 — across 629km surveyed only once every four years. The DfT rates overall
            performance <strong className="text-amber-400">AMBER</strong> but flags{' '}
            <strong className="text-rose-400">RED on best practice</strong>, and the council admits its current
            investment strategy is insufficient to improve residential roads.
          </p>

          {/* Key stat callout */}
          <div className="bg-rose-500/20 border border-rose-500/30 rounded-2xl p-6 max-w-md">
            <div className="text-6xl font-bold text-rose-400">31%</div>
            <div className="text-white font-medium mt-2">U-roads in RED condition (2024)</div>
            <div className="text-brand-300 text-sm mt-1">
              On 629.37km of unclassified roads — roughly 195km of estate streets and local routes — surveyed
              via coarse visual inspection on a four-year rotation, with no nationally recognised monitoring standard.
            </div>
          </div>
        </div>

        {/* Bottom chevrons */}
        <div
          className="absolute bottom-0 left-0 right-0 h-8 z-10"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #1e40af,
              #1e40af 40px,
              #f97316 40px,
              #f97316 80px
            )`,
          }}
        />
      </section>

      {/* The Condition Picture */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            What The Condition Data Shows
          </h2>
          <p className="text-surface-600 mb-8">
            Five years of survey data from Medway&apos;s own transparency report — classified roads broadly stable,
            unclassified roads deteriorating
          </p>

          <div className="bg-white border border-surface-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-brand-900 mb-2">Network size (845km total)</h3>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-4">
              <div className="text-center">
                <div className="text-2xl font-bold text-brand-900">100.47km</div>
                <div className="text-sm text-surface-600">A roads (11.9%)</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-brand-900">115.16km</div>
                <div className="text-sm text-surface-600">B and C roads (13.6%)</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-amber-600">629.37km</div>
                <div className="text-sm text-surface-600">U roads (74.5%)</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-brand-900">91,000+</div>
                <div className="text-sm text-surface-600">Other highway assets</div>
              </div>
            </div>
            <p className="text-surface-600 text-sm">
              Three-quarters of Medway&apos;s carriageway network is unclassified residential and local roads.
              The council also maintains 1,045.36km of footways, 130.36km of cycleways, 406 structures,
              35,535 gullies and more than 27,000 streetlights.
            </p>
          </div>

          {/* A-roads */}
          <div className="bg-surface-50 border border-surface-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-brand-900 mb-4">A-roads (100.47km): broadly stable after 2022 spike</h3>
            <div className="bg-white rounded-xl overflow-hidden border border-surface-200 mb-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-surface-100 border-b border-surface-200">
                    <th className="px-4 py-2 text-left font-semibold text-brand-900">Year</th>
                    <th className="px-4 py-2 text-center font-semibold text-rose-700">Red</th>
                    <th className="px-4 py-2 text-center font-semibold text-amber-700">Amber</th>
                    <th className="px-4 py-2 text-center font-semibold text-emerald-700">Green</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2020</td>
                    <td className="px-4 py-2 text-center">1.4%</td>
                    <td className="px-4 py-2 text-center">12.9%</td>
                    <td className="px-4 py-2 text-center">85.7%</td>
                  </tr>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2021</td>
                    <td className="px-4 py-2 text-center">2.3%</td>
                    <td className="px-4 py-2 text-center">13.8%</td>
                    <td className="px-4 py-2 text-center">83.9%</td>
                  </tr>
                  <tr className="border-b border-surface-100 bg-amber-50">
                    <td className="px-4 py-2 font-mono">2022</td>
                    <td className="px-4 py-2 text-center font-bold text-rose-600">5.0%</td>
                    <td className="px-4 py-2 text-center font-bold text-amber-600">20.9%</td>
                    <td className="px-4 py-2 text-center">74.2%</td>
                  </tr>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2023</td>
                    <td className="px-4 py-2 text-center">2.3%</td>
                    <td className="px-4 py-2 text-center">14.6%</td>
                    <td className="px-4 py-2 text-center">83.1%</td>
                  </tr>
                  <tr className="bg-surface-50">
                    <td className="px-4 py-2 font-mono font-bold">2024</td>
                    <td className="px-4 py-2 text-center font-bold">2.2%</td>
                    <td className="px-4 py-2 text-center font-bold">14.6%</td>
                    <td className="px-4 py-2 text-center font-bold text-emerald-600">83.2%</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-surface-600 text-sm">
              A-roads recovered after a 2022 deterioration spike (RED up to 5.0%, green down to 74.2%).
              Classified roads are SCANNER-surveyed annually — but A-roads are just 12% of the network.
            </p>
          </div>

          {/* B/C roads */}
          <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-amber-900 mb-4">B and C roads (115.16km): RED reported at 6% in 2024</h3>
            <div className="bg-white rounded-xl overflow-hidden border border-amber-200 mb-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-amber-100 border-b border-amber-200">
                    <th className="px-4 py-2 text-left font-semibold text-amber-900">Year</th>
                    <th className="px-4 py-2 text-center font-semibold text-rose-700">Red B and C reported</th>
                    <th className="px-4 py-2 text-center font-semibold text-amber-700">B-road RED</th>
                    <th className="px-4 py-2 text-center font-semibold text-amber-700">C-road RED</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2020</td>
                    <td className="px-4 py-2 text-center">4%</td>
                    <td className="px-4 py-2 text-center">3.0%</td>
                    <td className="px-4 py-2 text-center">4.5%</td>
                  </tr>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2021</td>
                    <td className="px-4 py-2 text-center">5%</td>
                    <td className="px-4 py-2 text-center">5.4%</td>
                    <td className="px-4 py-2 text-center">4.7%</td>
                  </tr>
                  <tr className="border-b border-amber-100 bg-rose-50">
                    <td className="px-4 py-2 font-mono">2022</td>
                    <td className="px-4 py-2 text-center font-bold text-rose-600">8%</td>
                    <td className="px-4 py-2 text-center font-bold text-rose-600">9.8%</td>
                    <td className="px-4 py-2 text-center">7.7%</td>
                  </tr>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2023</td>
                    <td className="px-4 py-2 text-center">6%</td>
                    <td className="px-4 py-2 text-center">5.0%</td>
                    <td className="px-4 py-2 text-center">5.7%</td>
                  </tr>
                  <tr className="bg-amber-50">
                    <td className="px-4 py-2 font-mono font-bold">2024</td>
                    <td className="px-4 py-2 text-center font-bold">6%</td>
                    <td className="px-4 py-2 text-center font-bold">4.4%</td>
                    <td className="px-4 py-2 text-center font-bold text-rose-600">6.5%</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-amber-800 font-medium">
              Combined B/C RED reporting doubled from 4% to 8% between 2020 and 2022, then settled at 6%.
              C-roads in RED condition reached 6.5% in 2024 — the highest individual class figure that year.
            </p>
          </div>

          {/* Spend context */}
          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-2xl font-bold mb-4">AMBER Spend — Matching Allocation, Not Exceeding It</h3>
            <div className="grid sm:grid-cols-3 gap-6 mb-6">
              <div>
                <div className="text-3xl font-bold text-amber-400">£5.024m</div>
                <div className="text-brand-300 text-sm">DfT capital allocation 2025/26</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-amber-400">£5.024m</div>
                <div className="text-brand-300 text-sm">Projected capital spend 2025/26</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-amber-400">60%</div>
                <div className="text-brand-300 text-sm">Projected preventative share</div>
              </div>
            </div>
            <p className="text-xl font-semibold text-white">
              Unlike authorities that invest well beyond their DfT allocation, Medway&apos;s projected 2025/26
              capital spend matches its allocation exactly — with{' '}
              <span className="text-amber-400">40% reactive maintenance</span> and just{' '}
              <span className="text-amber-400">~4.96km of resurfacing</span> planned across an 845km network.
            </p>
          </div>
        </div>
      </section>

      {/* The U-road blind spot */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            The 629km Four-Year Blind Spot
          </h2>
          <p className="text-surface-600 mb-8">
            74% of Medway&apos;s network is unclassified roads — coarse visual inspection on a four-year rotation
          </p>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full">
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-3 text-left text-sm font-semibold text-brand-900">Year</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">U-roads in RED condition</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2020</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">20%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2021</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">19%</td>
                </tr>
                <tr className="border-b border-surface-100 bg-rose-50">
                  <td className="px-6 py-4 font-mono text-brand-900">2022</td>
                  <td className="px-6 py-4 text-center text-surface-400 italic">34%* (unusable — post-winter survey)</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2023</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">22%</td>
                </tr>
                <tr>
                  <td className="px-6 py-4 font-mono font-bold text-brand-900">2024</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">31%</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">The Four-Year Rotation</h3>
              <p className="text-surface-600 text-sm mb-3">
                Medway divides its unclassified network into four areas for coarse visual inspection, with each
                area surveyed once every four years on a rotational basis. There is currently no nationally
                recognised standard for monitoring U-road condition.
              </p>
              <p className="text-surface-600 text-sm">
                At 31% RED in 2024, roughly <strong>195km of residential streets</strong> were in the worst
                condition category at the last survey — on a network where any given area may not be
                condition-assessed again for three more years.
              </p>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">*The 2022 Asterisk</h3>
              <p className="text-amber-800 text-sm mb-3">
                Medway states the 2022 condition survey was undertaken following the winter period at an
                unsuitable time of year. The data was incomparable and unusable — it did not accurately reflect
                the true condition of the network.
              </p>
              <p className="text-amber-800 text-sm">
                The jump from 19% to 34% and back to 22% is a survey-timing artefact, not evidence of
                rapid recovery. The council says so in its own footnotes.
              </p>
            </div>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-xl font-bold mb-4">Why This Matters For Section 58</h3>
            <p className="text-brand-200 mb-4">
              To rely on the Section 58 defence, a council must show it had a reasonable system for knowing
              the condition of its roads. For Medway&apos;s unclassified network, ask:
            </p>
            <ul className="text-brand-300 space-y-2 mb-6">
              <li>• When was your area last condition-surveyed — and when is the next rotation due?</li>
              <li>• If 31% of U-roads were RED at the last survey, what was done about yours?</li>
              <li>• How does coarse visual inspection every four years track deterioration between surveys?</li>
              <li>• Can the council claim detailed network knowledge without a nationally recognised U-road standard?</li>
            </ul>
            <p className="text-white font-medium">
              A council cannot claim comprehensive condition knowledge of a network it inspects on a four-year
              rotation using coarse visual methods — while admitting its strategy is insufficient to improve it.
            </p>
          </div>
        </div>
      </section>

      {/* The repair picture */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            44,689 Pothole Repairs in Four Years
          </h2>
          <p className="text-surface-600 mb-8">
            Published repair counts from Medway&apos;s transparency report — methodology changed in 2024/25
          </p>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full">
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-3 text-left text-sm font-semibold text-brand-900">Year</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Potholes repaired</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2021/22</td>
                  <td className="px-6 py-4 text-center">9,024</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2022/23</td>
                  <td className="px-6 py-4 text-center">12,040</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2023/24</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">16,373</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2024/25</td>
                  <td className="px-6 py-4 text-center">7,252</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2025/26 (to date)</td>
                  <td className="px-6 py-4 text-center text-surface-500">957</td>
                </tr>
                <tr className="bg-brand-50">
                  <td className="px-6 py-4 font-bold text-brand-900">Four-year total (2021/22–2024/25)</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">44,689</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-surface-50 rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">The Methodology Change</h3>
              <p className="text-surface-600 text-sm">
                Medway discontinued cold lay (Viafix) as a permanent repair in 2024/25, shifting to larger
                patching works and right-first-time repairs. The drop from 16,373 to 7,252 reflects a
                different repair approach — not proof that defects stopped forming on a network where 31%
                of U-roads remain in RED condition.
              </p>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">Below The Safety Threshold</h3>
              <p className="text-amber-800 text-sm">
                Medway uses additional funding to address clusters of defects that do not yet meet its
                safety intervention threshold or resurfacing criteria, but visually make the network look in
                worse condition than it structurally is. Defects below threshold can still damage vehicles —
                and prior reports prove the council knew about them.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* The council's own admission */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            The Council&apos;s Own Admission
          </h2>
          <p className="text-surface-600 mb-8">
            Medway publicly acknowledges its strategy is not keeping pace — in its own words
          </p>

          <div className="space-y-6 mb-8">
            <QuoteCard
              quote="The data above would suggest that even with the current strategy of investing more of the resurfacing budget on unclassified roads, it's insufficient to continue improving the condition of unclassified roads and keep the classified roads to a standard which meets or exceeds the national averages."
              source="Medway Council Highways Maintenance Transparency Report 2025/26"
            />
            <QuoteCard
              quote="Some worsening of the overall condition on the classified roads would be expected to achieve the required improvement on unclassified roads, we'll keep this under review and try and maintain a balance that enables all road classes to improve over time. To achieve this continual progress and move from a steady state to improvement, funding levels would need to meet those set out in our Lifecycle Plan to be able to satisfactorily maintain and improve the network."
              source="Medway Council Highways Maintenance Transparency Report 2025/26"
            />
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">What This Admission Means</h3>
              <p className="text-surface-600 text-sm mb-3">
                Medway formally acknowledges that current investment is insufficient to improve U-roads while
                maintaining classified roads to national averages. That is documented knowledge of a funding
                gap — not a neutral maintenance programme operating at steady state.
              </p>
              <p className="text-surface-600 text-sm">
                The council also expects some classified-road deterioration as a trade-off for U-road
                improvement — relevant context when arguing about inspection adequacy on your specific route.
              </p>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">Resurfacing Scale</h3>
              <p className="text-amber-800 text-sm mb-3">
                For 2025/26, Medway had completed 11 of 16 carriageway resurfacing schemes totalling 2.02km,
                with an expected final length of approximately 3.39km once all 16 are completed. Additional
                incentive funding is expected to resurface approximately 1.57km more.
              </p>
              <p className="text-amber-800 text-sm">
                That is roughly 4.96km of resurfacing across an 845km network — less than 0.6% of total
                carriageway length in a single year.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Best Practice RED */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Why Best Practice Is RED
          </h2>
          <p className="text-surface-600 mb-8">
            What Medway&apos;s own report reveals about asset management and data gaps
          </p>

          <div className="space-y-6 mb-8">
            <QuoteCard
              quote="Currently, there is no nationally recognised standard for monitoring the condition of the unclassified network. However, our suppliers conduct coarse visual inspections across these routes. Medway is divided into 4 areas for this process, with each area being surveyed once every 4 years on a rotational basis."
              source="Medway Council Highways Maintenance Transparency Report 2025/26"
            />
            <QuoteCard
              quote="The condition survey undertaken in 2022 was carried out during an unsuitable time of year, specifically following the winter period. As a result, the data collected was incomparable and unusable, as it did not accurately reflect the true condition of the road network at that time."
              source="Medway Council Highways Maintenance Transparency Report 2025/26"
            />
          </div>

          <div className="grid md:grid-cols-3 gap-6 mb-8">
            <div className="bg-rose-50 border border-rose-200 rounded-xl p-6">
              <h3 className="font-semibold text-rose-900 mb-3">Four-Year U-Road Cycle</h3>
              <p className="text-rose-800 text-sm">
                Coarse visual inspection on a four-year rotation across 629km — with no nationally recognised
                monitoring standard. Asset condition data on three-quarters of the network is inherently stale.
              </p>
            </div>
            <div className="bg-rose-50 border border-rose-200 rounded-xl p-6">
              <h3 className="font-semibold text-rose-900 mb-3">Unusable 2022 Data</h3>
              <p className="text-rose-800 text-sm">
                The council disclaims its own 2022 U-road survey as incomparable and unusable. A gap in
                reliable condition records weakens any blanket Section 58 defence tied to network knowledge.
              </p>
            </div>
            <div className="bg-rose-50 border border-rose-200 rounded-xl p-6">
              <h3 className="font-semibold text-rose-900 mb-3">AI Still Being Validated</h3>
              <p className="text-rose-800 text-sm">
                Medway has implemented AI technology for highway safety inspections but is still comparing
                AI-derived condition data with its current supplier to assess alignment and accuracy — the
                enhanced frequency promised depends on that validation completing.
              </p>
            </div>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-2xl font-bold mb-4">Triple AMBER, Best Practice RED</h3>
            <div className="grid sm:grid-cols-3 gap-6 mb-6">
              <div>
                <div className="text-3xl font-bold text-amber-400">AMBER</div>
                <div className="text-brand-300 text-sm">Overall and condition</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-amber-400">AMBER</div>
                <div className="text-brand-300 text-sm">Spend — matches allocation, not exceeds</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-rose-400">RED</div>
                <div className="text-brand-300 text-sm">Best practice — data and process gaps</div>
              </div>
            </div>
            <p className="text-xl font-semibold text-white">
              Medway is not under-funded like some RED-spend authorities — but the DfT&apos;s{' '}
              <span className="text-rose-400">RED best-practice flag</span> signals weaknesses in how the
              council measures, records and manages its network. That matters when the council argues it
              could not reasonably have known about your defect.
            </p>
          </div>
        </div>
      </section>

      {/* What AMBER + RED best practice means for claims */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Claiming Against Medway&apos;s RED Best-Practice Scorecard
          </h2>
          <p className="text-surface-600 mb-8">
            Honest assessment: triple AMBER is not Derbyshire — but RED best practice opens specific angles
          </p>

          <div className="grid md:grid-cols-2 gap-8 mb-8">
            <div className="bg-white rounded-2xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-4">What Works In The Council&apos;s Favour</h3>
              <ul className="text-surface-700 space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> AMBER spend — capital spend matches DfT allocation in 2025/26
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> A-roads broadly stable at 2.2% RED in 2024 after annual SCANNER surveys
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> 60% preventative maintenance projected for 2025/26
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> Well Managed Highway Infrastructure code adopted — risk-based approach documented
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> Discontinued cold-lay Viafix — shifting to permanent patching
                </li>
              </ul>
              <p className="text-surface-500 text-sm mt-4 italic">
                Expect a structured Section 58 defence on A-roads with recent SCANNER data. Generic claims will struggle.
              </p>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6">
              <h3 className="font-semibold text-amber-900 mb-4">What Works In Yours</h3>
              <ul className="text-amber-800 space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> RED best practice — four-year U-road surveys, unusable 2022 data, AI unvalidated
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> 31% of U-roads in RED condition in 2024 — up from 20% in 2020
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> 629km surveyed on a four-year coarse visual rotation — no national U-road standard
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> Council admits current strategy insufficient to improve U-roads
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> Defect clusters below safety threshold acknowledged but not automatically repaired
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> 44,689 pothole repairs in four years — defects still forming between inspections
                </li>
              </ul>
            </div>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-xl font-bold mb-4">The Winning Strategy Here Is Specificity</h3>
            <p className="text-brand-200 mb-4">
              Against a council with documented asset-management processes, your claim lives or dies on the{' '}
              <strong className="text-white">specific defect</strong>:
            </p>
            <ul className="text-brand-300 space-y-2 mb-6">
              <li>• Prior reports of the same pothole (Medway&apos;s online form, FixMyStreet) — proof of actual notice</li>
              <li>• Photos showing the defect&apos;s size, depth and visible age (weathered edges, previous patching)</li>
              <li>• The road&apos;s class — on a U-road, the four-year survey gap and 31% RED rate are your strongest structural arguments</li>
              <li>• Whether the defect met Medway&apos;s safety threshold — and if reported, why it was left unrepaired</li>
            </ul>
            <p className="text-white font-medium">
              Mac builds exactly this case: he searches for prior reports, assesses your photo evidence, and
              cites Medway&apos;s own transparency data where it helps you.
            </p>
          </div>
        </div>
      </section>

      {/* Report a pothole CTA */}
      <section className="py-16 bg-white border-y border-surface-200">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display text-2xl font-bold text-brand-950 mb-4">
            Report the pothole to Medway first
          </h2>
          <p className="text-surface-600 mb-6">
            Medway&apos;s own guidance states that if you have experienced injury or damage because of a pothole,
            you can make a claim — but you must report the pothole to the council first. Reporting creates a
            dated record useful if the defect was known before your incident, or if the council failed to repair
            it within a reasonable time.
          </p>
          <a
            href="https://www.medway.gov.uk/info/200225/report-a-road-problem/552/roads-and-potholes"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center px-8 py-4 bg-brand-900 text-white font-semibold rounded-xl hover:bg-brand-800 transition-colors"
          >
            Report a pothole to Medway Council
            <svg className="ml-2 w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
            </svg>
          </a>
          <p className="text-surface-500 text-sm mt-4">
            Use the online form with location, photos and contact details. Keep your reference number and any confirmation emails.
          </p>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-brand-950 text-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display text-3xl font-bold mb-4">
            Hit a Pothole in Medway?
          </h2>
          <p className="text-xl text-brand-200 mb-8">
            A RED best-practice scorecard demands a precise claim. £35 for a professional claim pack.
          </p>

          <div className="grid sm:grid-cols-2 gap-6 mb-8">
            <div className="bg-brand-900/50 rounded-xl p-6 border border-brand-800">
              <h3 className="font-semibold text-brand-300 mb-3">DIY claim</h3>
              <ul className="text-sm text-brand-400 space-y-1 text-left">
                <li>• Submit photos and invoices</li>
                <li>• Use generic template letter</li>
                <li>• No four-year U-road survey gap cited</li>
                <li>• No 31% RED condition data referenced</li>
                <li>• No prior-report search</li>
              </ul>
            </div>
            <div className="bg-accent-500/20 rounded-xl p-6 border border-accent-500/30">
              <h3 className="font-semibold text-white mb-3">Professional claim pack</h3>
              <ul className="text-sm text-brand-200 space-y-1 text-left">
                <li>✅ 31% U-road RED condition documented</li>
                <li>✅ Four-year coarse visual survey gap argued</li>
                <li>✅ RED best-practice scorecard and council admission cited</li>
                <li>✅ Prior reports searched and attached</li>
                <li>✅ Section 58 rebuttal tailored to Medway</li>
              </ul>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/check?council=medway"
              className="inline-flex items-center justify-center px-8 py-4 bg-accent-500 text-white font-semibold rounded-xl hover:bg-accent-600 transition-all hover:shadow-lg hover:shadow-accent-500/25"
            >
              Order My Medway Claim Pack — £35
              <svg className="ml-2 w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </Link>
            <Link
              to="/road-maintenance-report"
              className="inline-flex items-center justify-center px-8 py-4 bg-white/10 text-white font-semibold rounded-xl hover:bg-white/20 transition-colors"
            >
              View All Council Ratings
            </Link>
          </div>

          <p className="text-brand-400 text-sm mt-6">
            No percentage fees. You keep 100% of any compensation.
          </p>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-2xl font-bold text-brand-950 mb-8">
            Frequently Asked Questions
          </h2>

          <div className="space-y-6">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Medway has a RED best-practice scorecard — can I still claim?
              </h3>
              <p className="text-surface-600 text-sm">
                Yes. The DfT Best Practice rating is RED, and Medway&apos;s overall rating is AMBER — but your
                claim still turns on whether the specific defect that damaged your vehicle was reasonably
                inspected and repaired under Section 58, not on scorecard colours alone. Medway&apos;s own
                transparency report shows 31% of U-roads in RED condition in 2024, coarse visual inspections
                on a four-year rotation across 629km of residential roads, and the council admitting its
                current strategy is insufficient to improve unclassified roads.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                What if my pothole was on a residential or unclassified road?
              </h3>
              <p className="text-surface-600 text-sm">
                U-roads make up 629.37km — 74% of Medway&apos;s 845km network. The council conducts coarse visual
                inspections divided into four areas, with each area surveyed once every four years on a
                rotational basis. There is no nationally recognised standard for U-road condition monitoring.
                At the last comparable surveys, 19–31% of U-roads were in RED condition — roughly 120–195km
                of estate streets, closes and local routes.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Pothole repairs fell from 16,373 to 7,252 — does that mean the roads are fixed?
              </h3>
              <p className="text-surface-600 text-sm">
                No. Medway changed its repair methodology in 2024/25, discontinuing cold lay (Viafix) as a
                permanent repair and shifting to larger patching works. The lower published repair count reflects
                a different counting approach, not elimination of defects. The council still acknowledges
                clusters of defects below its safety intervention threshold that visually make the network look
                in worse condition than it structurally is.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Why is the 2022 U-road figure of 34% unreliable?
              </h3>
              <p className="text-surface-600 text-sm">
                Medway&apos;s own report states the 2022 condition survey was carried out during an unsuitable
                time of year, specifically following the winter period. As a result, the data collected was
                incomparable and unusable, as it did not accurately reflect the true condition of the road
                network at that time. Do not treat the 34% spike as evidence of sudden recovery or deterioration.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Medway says some defects do not meet its safety threshold — does that help my claim?
              </h3>
              <p className="text-surface-600 text-sm">
                It can. Medway uses additional highways funding to address clusters of defects that do not yet
                meet its safety intervention threshold or resurfacing criteria, but visually make the network
                look in worse condition than it structurally is. If the pothole that damaged your vehicle was
                visible, reportable and left unrepaired, the council&apos;s own admission that sub-threshold
                clusters persist is relevant context — especially combined with prior reports and photographic
                evidence.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Do I have to report the pothole to Medway before claiming?
              </h3>
              <p className="text-surface-600 text-sm">
                Medway&apos;s own report-a-pothole guidance states that if you have experienced injury or damage
                because of a pothole, you can make a claim to Medway Council for personal injury or damage — but
                you must do this once you have reported the pothole to the council. Reporting creates a dated
                record that strengthens your case if the defect was known before your incident or left unrepaired
                within a reasonable time.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Attribution Footer */}
      <section className="py-8 bg-surface-100 border-t border-surface-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs text-surface-500 text-center">
            Data sources:{' '}
            <a
              href="https://www.gov.uk/government/publications/local-road-maintenance-ratings/local-road-maintenance-ratings-2025-to-2026"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Department for Transport — Local Road Maintenance Ratings 2025 to 2026
            </a>{' '}
            |{' '}
            <a
              href="https://www.medway.gov.uk/info/200249/roadworks_in_medway/526/roadwork_schemes/3"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Medway Council Highways Maintenance Transparency Report (2025/26)
            </a>
            . Contains public sector information licensed under the{' '}
            <a
              href="https://www.nationalarchives.gov.uk/doc/open-government-licence/version/3/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Open Government Licence v3.0
            </a>
            .
          </p>
        </div>
      </section>
    </div>
  );
}
